/**
 * AutocompleteService - Phase 4.5
 * Provides city/address autocomplete using backend proxy to Google Places API.
 * Never exposes API key to frontend.
 * Phase 40: Integrates with FavoritesService — favorites shown at top of suggestions.
 */

const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
import maplibregl from "maplibre-gl";

import { favoritesService } from './FavoritesService.js';
import * as units from './units.js';
import { currentMarket } from './market.js';

export class AutocompleteService {
    constructor() {
        this.debounceTimers = {};
        this.activeDropdown = null;
        this.resolvedPlaces = {}; // inputId → { placeId, lat, lng, city, state }
        this.onSelectCallbacks = {}; // inputId → fn(place) for multi-add inputs (e.g. avoid-city)
    }

    /**
     * Attach autocomplete to an input element
     * @param {string} inputId - ID of the input element
     * @param {Object} [options]
     * @param {(place: Object) => void} [options.onSelect] - Called with the
     *   resolved place after a selection, and the input is cleared right
     *   after for another entry, instead of the usual "leave the picked
     *   description in the box" behavior. For inputs that collect a LIST
     *   (currently only the avoid-city input) rather than a single value.
     */
    attach(inputId, options = {}) {
        const input = document.getElementById(inputId);
        if (!input) return;
        if (options.onSelect) this.onSelectCallbacks[inputId] = options.onSelect;

        // Create dropdown container
        const dropdown = document.createElement('div');
        dropdown.className = 'autocomplete-dropdown';
        dropdown.id = `${inputId}-dropdown`;
        dropdown.style.display = 'none';
        input.parentElement.style.position = 'relative';
        input.parentElement.appendChild(dropdown);

        // Listen for input with debounce
        input.addEventListener('input', () => {
            this.resolvedPlaces[inputId] = null; // Clear resolved place on new input
            this.debounce(inputId, () => this.onInput(inputId, input.value), 300);
        });

        // Close dropdown on blur (with delay for click to register)
        input.addEventListener('blur', () => {
            setTimeout(() => { dropdown.style.display = 'none'; }, 200);
        });

        // Reopen on focus if there's text
        input.addEventListener('focus', () => {
            if (input.value.length >= 3 && dropdown.children.length > 0) {
                dropdown.style.display = 'block';
            }
        });

        // Keyboard navigation
        input.addEventListener('keydown', (e) => {
            this.handleKeydown(e, dropdown, inputId);
        });
    }

    /**
     * Handle input change — fetch suggestions
     */
    async onInput(inputId, value) {
        const dropdown = document.getElementById(`${inputId}-dropdown`);
        if (!dropdown) return;

        if (value.length < 1) {
            dropdown.style.display = 'none';
            dropdown.innerHTML = '';
            return;
        }

        // Check favorites first (even for 1-2 char input)
        const favMatches = favoritesService.match(value);
        const favHtml = favMatches.map((f, i) => `
            <div class="autocomplete-item autocomplete-favorite${i === 0 && value.length < 3 ? ' active' : ''}" data-place-id="fav_${f.id}" data-description="${f.address || f.label}" data-lat="${f.lat}" data-lng="${f.lng}" data-is-favorite="true">
                <span class="autocomplete-fav-icon">⭐</span>
                <span class="autocomplete-main">${f.label}</span>
                <span class="autocomplete-secondary">${f.address || ''}</span>
            </div>
        `).join('');

        // If input is too short for Places API, just show favorites
        if (value.length < 3) {
            if (favHtml) {
                dropdown.innerHTML = favHtml;
                this.wireDropdownClicks(dropdown, inputId);
                dropdown.style.display = 'block';
            } else {
                dropdown.style.display = 'none';
                dropdown.innerHTML = '';
            }
            return;
        }

        try {
            // Include user location for proximity bias
            const loc = this.resolvedPlaces?.['start'] || window._lastGpsPosition || null;
            const locParam = loc ? `&lat=${loc.lat}&lng=${loc.lng}` : '';
            // Optional auth: lets the backend widen search countries per account.
            const headers = {};
            const auth = window.authService;
            if (auth && await auth.refreshIfNeeded()) {
                const token = auth.getAccessToken();
                if (token) headers.Authorization = `Bearer ${token}`;
            }
            // market: which country to search when there's no account to go by.
            const response = await fetch(`${API_URL}/api/autocomplete?input=${encodeURIComponent(value)}${locParam}&market=${currentMarket()}`, { headers });
            const data = await response.json();

            let placesHtml = '';
            if (data.status === 'success' && data.suggestions.length) {
 this._lastSuggestions = data.suggestions;
                placesHtml = data.suggestions.map((s, i) => `
                    <div class="autocomplete-item${!favHtml && i === 0 ? ' active' : ''}" data-place-id="${s.placeId}" data-description="${s.description}">
                        <span class="autocomplete-main">${s.mainText}</span>
                        <span class="autocomplete-secondary">${s.secondaryText}</span>
                    </div>
                `).join('');
            }

            const combined = favHtml + placesHtml;
            if (!combined) {
                dropdown.style.display = 'none';
                dropdown.innerHTML = '';
                return;
            }

            // "Show all nearby" (a separate places-search call for a
            // detected business chain) is gone now that autocomplete
            // itself returns real nearby businesses via Suggest -- it was
            // a workaround for AutocompleteCommand never finding POIs at
            // all, no longer needed once the search itself was fixed.
            dropdown.innerHTML = combined + `
                <div class="autocomplete-item autocomplete-map-pick" data-action="choose-on-map" data-query="${value}">
                    <span class="autocomplete-main">📍 Choose on map</span>
                </div>
            `;
            // Set first item active if nothing is active
            if (!dropdown.querySelector('.active') && dropdown.children.length > 0) {
                dropdown.children[0].classList.add('active');
            }
            this.wireDropdownClicks(dropdown, inputId);
            dropdown.style.display = 'block';
        } catch (err) {
            console.warn('Autocomplete fetch failed:', err);
            // Still show favorites if places API fails
            if (favHtml) {
                dropdown.innerHTML = favHtml;
                this.wireDropdownClicks(dropdown, inputId);
                dropdown.style.display = 'block';
            } else {
                dropdown.style.display = 'none';
            }
        }
    }

    /**
     * Wire click handlers on dropdown items
     */
    wireDropdownClicks(dropdown, inputId) {
        dropdown.querySelectorAll('.autocomplete-item').forEach(item => {
            item.addEventListener('mousedown', (e) => {
                e.preventDefault();
                if (item.dataset.action === 'choose-on-map') {
                    dropdown.style.display = 'none';
                    // Was reusing the plain autocomplete dropdown's own
                    // suggestions -- soft-biased, not restricted, so a
                    // generic query (any bare city name, an uncommon
                    // business) could pin candidates hundreds of miles
                    // apart on the map. "Choose on map" is inherently a
                    // "point at something near me" interaction, so search
                    // fresh via places-search's hard Filter.Circle instead.
                    this.startMapSelectionForQuery(inputId, item.dataset.query);
                } else if (item.dataset.isFavorite === 'true') {
                    this.selectFavorite(inputId, item.dataset);
                } else {
                    this.selectItem(inputId, item.dataset.placeId, item.dataset.description);
                }
            });
        });
    }

    /**
     * Select a favorite — directly set coordinates without Places API call
     */
    selectFavorite(inputId, data) {
        const input = document.getElementById(inputId);
        const dropdown = document.getElementById(`${inputId}-dropdown`);

        input.value = data.description || data.label;
 input.blur();
        dropdown.style.display = 'none';

        // Set resolved place directly — no API call needed
        this.resolvedPlaces[inputId] = {
            lat: parseFloat(data.lat),
            lng: parseFloat(data.lng),
            formattedAddress: data.description,
            source: 'favorite'
        };
        console.log(`⭐ Resolved ${inputId} from favorite:`, this.resolvedPlaces[inputId]);
    }

    /**
     * Handle keyboard navigation in dropdown
     */
    handleKeydown(e, dropdown, inputId) {
        if (dropdown.style.display === 'none') return;

        const items = dropdown.querySelectorAll('.autocomplete-item');
        const active = dropdown.querySelector('.autocomplete-item.active');
        let activeIndex = Array.from(items).indexOf(active);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (activeIndex < items.length - 1) {
                items[activeIndex]?.classList.remove('active');
                items[activeIndex + 1]?.classList.add('active');
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (activeIndex > 0) {
                items[activeIndex]?.classList.remove('active');
                items[activeIndex - 1]?.classList.add('active');
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (active) {
                this.selectItem(inputId, active.dataset.placeId, active.dataset.description);
            }
        } else if (e.key === 'Escape') {
            dropdown.style.display = 'none';
        }
    }

    /**
     * Select an autocomplete item — resolve to coordinates
     */
    async selectItem(inputId, placeId, description) {
        const input = document.getElementById(inputId);
        const dropdown = document.getElementById(`${inputId}-dropdown`);

        // Set input value to the description
        input.value = description;
 input.blur();
        dropdown.style.display = 'none';

        // Resolve place to coordinates
        try {
            const response = await fetch(`${API_URL}/api/place?placeId=${encodeURIComponent(placeId)}`);
            const data = await response.json();

            if (data.status === 'success') {
                this.resolvedPlaces[inputId] = data.place;
                console.log(`📍 Resolved ${inputId}:`, data.place);

                const onSelect = this.onSelectCallbacks[inputId];
                if (onSelect) {
                    onSelect(data.place);
                    this.resolvedPlaces[inputId] = null;
                    if (input) input.value = '';
                }
            }
        } catch (err) {
            console.warn('Place resolution failed:', err);
        }
    }

    /**
     * Get resolved coordinates for an input
     * @param {string} inputId
     * @returns {{ lat: number, lng: number, city: string, state: string } | null}
     */
    getResolvedPlace(inputId) {
        return this.resolvedPlaces[inputId] || null;
    }

    /**
     * Debounce helper
     */
    debounce(key, fn, delay) {
        if (this.debounceTimers[key]) {
            clearTimeout(this.debounceTimers[key]);
        }
        this.debounceTimers[key] = setTimeout(fn, delay);
    }

    /**
     * Entry point for the plain autocomplete dropdown's "Choose on map"
     * item: searches nearby fresh via places-search (hard Filter.Circle)
     * instead of reusing the dropdown's own soft-biased suggestions, so
     * the pins shown are always actually nearby regardless of how broadly
     * the typed query could otherwise match. Results already carry
     * resolved lat/lng, so startMapSelection can skip the per-pin
     * /api/place lookup for these.
     */
    async startMapSelectionForQuery(inputId, query) {
        const loc = this.resolvedPlaces?.['start'] || window._lastGpsPosition || null;
        if (!loc) {
            alert('Use GPS button first to set your location');
            return;
        }

        let suggestions = [];
        try {
            const response = await fetch(`${API_URL}/api/places-search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query, lat: loc.lat, lng: loc.lng })
            });
            const data = await response.json();
            if (data.status === 'success' && data.results?.length) {
                suggestions = data.results.map(r => ({
                    placeId: r.placeId,
                    description: `${r.name}, ${r.address}`,
                    mainText: r.name,
                    secondaryText: `${r.address} · ${units.distanceText(r.distanceMiles)}`,
                    _resolved: { lat: r.lat, lng: r.lng }
                }));
            }
        } catch (err) {
            console.warn('Nearby search for map selection failed:', err);
        }

        if (!suggestions.length) {
            alert('No nearby results found — tap directly on the map instead.');
        }
        this.startMapSelection(inputId, suggestions);
    }

    /**
     * Removes markers and the map click-listener left over from a
     * previous, never-completed "choose on map" session (e.g. the user
     * searched again instead of tapping a pin or the map). Without this,
     * starting a fresh search while an earlier session's pins were still
     * showing left both sets on the map together -- confirmed: searching
     * Walmart right after Home Depot showed both.
     */
    _clearActiveMapSelection() {
        const active = this._activeMapSelection;
        if (!active) return;
        active.markers.forEach(m => { try { m.remove(); } catch (e) { /* already gone */ } });
        if (active.onMapClick) {
            try { active.mapInstance.off('click', active.onMapClick); } catch (e) { /* ignore */ }
        }
        this._activeMapSelection = null;
    }

    /**
     * Enter "choose on map" mode. Shows search result markers on map.
     * Next tap on a marker or the map sets the coordinates for the given input field.
     */
    startMapSelection(inputId, suggestions = []) {
        const input = document.getElementById(inputId);
        if (input) input.value = '';

        const mapInstance = window.uiController?.mapService?.map || window.mapService?.map;
        if (!mapInstance) {
            alert('Map not available');
            return;
        }

        this._clearActiveMapSelection();

        // Switch to map view on mobile -- go through the app's own
        // toggle function (window.toggleMobileSidebar, main.js) instead
        // of flipping the CSS classes directly. Doing it directly left
        // the right-edge tab still saying "MAP" while the sidebar was
        // actually hidden and the map fullscreen, since only that
        // function knows to also update the tab's label/the mobile
        // sidebar button.
        const sidebar = document.querySelector('.sidebar');
        const enteredMapView = !!(sidebar && !sidebar.classList.contains('mobile-hidden'));
        if (enteredMapView && window.toggleMobileSidebar) {
            window.toggleMobileSidebar();
        }

        // Place markers for autocomplete suggestions
        const markers = [];
        const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

        const resolveAndPlace = async () => {
            for (const s of suggestions.slice(0, 5)) {
                try {
                    // places-search results (startMapSelectionForQuery)
                    // already carry resolved coordinates -- skip the
                    // redundant /api/place round-trip for those.
                    let place = s._resolved;
                    if (!place) {
                        const resp = await fetch(`${API_URL}/api/place?placeId=${encodeURIComponent(s.placeId)}`);
                        const data = await resp.json();
                        if (data.status !== 'success' || !data.place) continue;
                        place = data.place;
                    }

                    const el = document.createElement('div');
                    el.style.cssText = 'background:#F5A623;color:#1a1a2e;padding:4px 8px;border-radius:12px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.3);';
                    el.textContent = s.mainText || s.description;
                    el.title = s.description;

                    const marker = new maplibregl.Marker({ element: el })
                        .setLngLat([place.lng, place.lat])
                        .addTo(mapInstance);

                    el.addEventListener('click', (e) => {
                        e.stopPropagation();
                        selectPin(place.lat, place.lng, s.description);
                    });

                    markers.push(marker);
                } catch (e) { /* skip */ }
            }

            if (markers.length > 0) {
                const bounds = new maplibregl.LngLatBounds();
                markers.forEach(m => bounds.extend(m.getLngLat()));
                mapInstance.fitBounds(bounds, { padding: 60, maxZoom: 12 });
            }
        };

        if (suggestions.length > 0) resolveAndPlace();

        // Go back to planning view -- only flip back if we were the one
        // that entered map view in the first place, and go through the
        // same toggle function used to enter it so the tab label/mobile
        // button state stay in sync.
        const goBackToPlanning = () => {
            this._activeMapSelection = null;
            if (enteredMapView && window.toggleMobileSidebar) {
                window.toggleMobileSidebar();
            }
        };

        // When a pin is tapped
        const selectPin = (lat, lng, description) => {
            mapInstance.off('click', onMapClick);
            markers.forEach(m => m.remove());

            this.resolvedPlaces[inputId] = {
                lat, lng,
                formattedAddress: description,
                source: 'map-pick'
            };
            if (input) input.value = description;
            goBackToPlanning();
        };

        // Map tap (not on a pin)
        const onMapClick = async (e) => {
            const lat = e.lngLat.lat;
            const lng = e.lngLat.lng;

            mapInstance.off('click', onMapClick);
            markers.forEach(m => m.remove());

            this.resolvedPlaces[inputId] = {
                lat, lng,
                formattedAddress: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                source: 'map-pick'
            };
            if (input) input.value = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`);
                const data = await res.json();
                if (data.display_name) {
                    const address = data.display_name.split(',').slice(0, 3).join(',').trim();
                    if (input) input.value = address;
                    this.resolvedPlaces[inputId].formattedAddress = address;
                }
            } catch (e) { /* keep raw */ }

            goBackToPlanning();
        };

        mapInstance.on('click', onMapClick);
        this._activeMapSelection = { mapInstance, markers, onMapClick };
    }
}

export const autocompleteService = new AutocompleteService();
